import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Zap, Users, Eye, ArrowRight, CheckCircle, Globe, Award } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { siteContent } from '../data';

const About = () => {
  const iconMap = { Shield, Zap, Users, Eye };

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="pt-32 pb-16 relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-cyan-900/20 via-black to-black"></div>
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-20 w-72 h-72 bg-green-500/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full mb-6 backdrop-blur-sm">
              <Award className="w-4 h-4 text-cyan-400 mr-2" />
              <span className="text-cyan-400 text-sm font-medium">Trusted Security Partner</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">{siteContent.about.title}</h1>
            <p className="text-xl text-cyan-400 mb-8">{siteContent.about.subtitle}</p>
            <p className="text-lg text-gray-300 leading-relaxed">{siteContent.about.mission}</p>
          </div>
        </div>
      </section>

      {/* Stats Banner */}
      <section className="py-12 bg-gradient-to-r from-cyan-900/20 via-cyan-900/10 to-green-900/20 border-y border-cyan-900/30">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {siteContent.stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-green-400 mb-1">
                  {stat.number}
                </div>
                <div className="text-gray-400 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Our Core Values</h2>
            <p className="text-gray-400 text-lg">The principles that guide everything we do</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {siteContent.about.values.map((value, idx) => {
              const Icon = iconMap[value.icon] || Shield;
              return (
                <Card key={idx} className="bg-gray-900/30 border-cyan-900/30 backdrop-blur-sm hover:bg-gray-900/50 transition-all duration-300 hover:border-cyan-500/50 hover:shadow-lg hover:shadow-cyan-500/10">
                  <CardHeader>
                    <div className="w-14 h-14 bg-gradient-to-br from-cyan-500/20 to-green-500/20 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="w-7 h-7 text-cyan-400" />
                    </div>
                    <CardTitle className="text-white text-2xl">{value.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-400 leading-relaxed">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Trust Us Section */}
      <section className="py-20 bg-gradient-to-b from-black via-gray-950 to-black">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold text-white mb-6">Why Trust SiteGuarder?</h2>
                <p className="text-gray-300 mb-8 leading-relaxed">
                  We combine cutting-edge technology with human expertise to deliver unparalleled protection for your digital assets. Our team has protected hundreds of organizations from cyber threats.
                </p>
                <ul className="space-y-4">
                  {[
                    'Industry-leading threat detection rates',
                    'Certified security professionals',
                    'Global enforcement network',
                    '24/7 dedicated support team',
                    'Transparent reporting and communication'
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Shield, label: 'Advanced Protection' },
                  { icon: Globe, label: 'Global Coverage' },
                  { icon: Zap, label: 'Fast Response' },
                  { icon: Users, label: 'Expert Team' }
                ].map((item, idx) => (
                  <div key={idx} className="bg-gray-900/30 border border-cyan-900/30 rounded-2xl p-6 text-center hover:border-cyan-500/50 transition-all duration-300">
                    <item.icon className="w-10 h-10 text-cyan-400 mx-auto mb-3" />
                    <p className="text-white text-sm font-medium">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="bg-gradient-to-r from-cyan-900/30 to-green-900/30 backdrop-blur-sm rounded-3xl p-12 border border-cyan-900/50 max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Ready to Work With Us?</h2>
            <p className="text-gray-300 mb-8">Let's discuss how we can protect your business</p>
            <Link to="/contact">
              <Button size="lg" className="bg-gradient-to-r from-cyan-500 to-green-500 hover:from-cyan-600 hover:to-green-600 text-black font-semibold px-8 py-6 text-lg rounded-xl shadow-lg shadow-cyan-500/25">
                Get In Touch
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;